import { api } from './api'

export const DEFAULT_TRIANGLE_ID = '00000000-0000-0000-0000-000000000001'

export type DiagnosticAmountType = 'Incurred' | 'Paid' | 'Count'

export interface TriangleGroup {
  id: string
  name: string
  selected_months?: string
  selections?: Record<string, {
    excluded_origin_periods?: string[]
    excluded_dev_periods?: number[]
    custom_link_ratios?: Array<{
      name?: string
      custom_type?: string
    }>
    custom_ultimate_claims?: Array<{
      name: string
      ultimate_claims: number[]
    }>
  }>
}

interface TriangleData {
  origin_labels: string[]
  dev_labels: string[]
  values: (number | null)[][]
}

interface UltimateClaims {
  ultimate_claims_type?: string[]
  paid_claims_to_ultimate?: number[]
  incurred_claims_to_ultimate?: number[]
  apriori_ultimate?: number[]
  cl_paid_ultimate?: number[]
  cl_incurred_ultimate?: number[]
  bf_paid_ultimate?: number[]
  bf_incurred_ultimate?: number[]
}

export interface TriangulationResponse {
  triangle: TriangleData
  link_ratios_values: (number | null)[][]
  link_ratio_names?: string[]
  link_ratios_selection: number[]
  ultimate_claims_selection: number[]
  ultimate_claims: UltimateClaims
  triangle_metadata?: TriangleGroup
}

export interface DiagnosticPoint {
  dev: number
  /** null renders a gap — excluded or missing triangle cells. */
  value: number | null
}

export interface DiagnosticSeries {
  name: string
  points: DiagnosticPoint[]
  selected?: boolean
}

const ultimateColumns = [
  'Incurred Claims',
  'Paid Claims',
  'A-priori',
  'CL Paid',
  'CL Incurred',
  'BF Paid',
  'BF Incurred',
] as const

const selectionKey = (amountType: DiagnosticAmountType) =>
  amountType === 'Paid'
    ? 'ultimate_claims_paid'
    : amountType === 'Count'
      ? 'ultimate_claims_count'
      : 'ultimate_claims_incurred'

const exclusionKey = (amountType: DiagnosticAmountType) =>
  amountType === 'Paid'
    ? 'exclusions_paid'
    : amountType === 'Count'
      ? 'exclusions_count'
      : 'exclusions_incurred'

const linkRatioKey = (amountType: DiagnosticAmountType) =>
  amountType === 'Paid'
    ? 'link_ratios_paid'
    : amountType === 'Count'
      ? 'link_ratios_count'
      : 'link_ratios_incurred'

const getLinkRatioNames = (
  response: TriangulationResponse,
  amountType: DiagnosticAmountType
): string[] => {
  if (response.link_ratio_names?.length === response.link_ratios_values.length) {
    return response.link_ratio_names
  }

  const names = ['Weighted Average', 'Simple Average']
  const custom =
    response.triangle_metadata?.selections?.[linkRatioKey(amountType)]?.custom_link_ratios ?? []
  names.push(...custom.map((ratio, index) => ratio.name?.trim() || `Custom ${index + 1}`))
  while (names.length < response.link_ratios_values.length) {
    names.push(`Method ${names.length + 1}`)
  }
  return names
}

const getUltimateColumns = (
  response: TriangulationResponse,
  amountType: DiagnosticAmountType
): Record<string, number[]> => {
  const claims = response.ultimate_claims
  const custom =
    response.triangle_metadata?.selections?.[selectionKey(amountType)]?.custom_ultimate_claims ?? []

  return {
    'Incurred Claims': claims.incurred_claims_to_ultimate ?? [],
    'Paid Claims': claims.paid_claims_to_ultimate ?? [],
    'A-priori': claims.apriori_ultimate ?? [],
    'CL Paid': claims.cl_paid_ultimate ?? [],
    'CL Incurred': claims.cl_incurred_ultimate ?? [],
    'BF Paid': claims.bf_paid_ultimate ?? [],
    'BF Incurred': claims.bf_incurred_ultimate ?? [],
    ...Object.fromEntries(custom.map(column => [column.name, column.ultimate_claims])),
  }
}

/**
 * Backend ultimate_claims_selection indices (see UltimateSelection / claimKeyForSelectionIndex):
 * 0 Paid, 1 OS→Paid, 2 Incurred, 3 A-priori, 4–7 CL/BF, ≥8 custom (FE index = b - 1).
 */
const claimKeyForBackendSelection = (backendIndex: number, customNames: string[]): string => {
  if (backendIndex === 0 || backendIndex === 1) return 'Paid Claims'
  if (backendIndex === 2) return 'Incurred Claims'
  if (backendIndex < 3) return 'CL Incurred'
  const feIndex = backendIndex - 1
  return [...ultimateColumns, ...customNames][feIndex] ?? 'CL Incurred'
}

const getSelectedUltimateNames = (
  response: TriangulationResponse,
  amountType: DiagnosticAmountType
): string[] => {
  const customNames =
    response.triangle_metadata?.selections?.[selectionKey(amountType)]?.custom_ultimate_claims
      ?.map(column => column.name) ?? []
  const columnOrder = [...ultimateColumns, ...customNames]
  const apiTypes = response.ultimate_claims.ultimate_claims_type ?? []
  const selections = response.ultimate_claims_selection ?? []

  if (selections.length === 0) {
    return apiTypes.map(name => (columnOrder.includes(name) ? name : 'CL Incurred'))
  }

  return selections.map(index => claimKeyForBackendSelection(index, customNames))
}

const cumulativeFactors = (ratios: number[]): number[] =>
  ratios.map((_, index) =>
    ratios.slice(index).reduce((product, value) => product * (value || 1), 1)
  )

export const buildDiagnosticSeries = (
  response: TriangulationResponse,
  amountType: DiagnosticAmountType
): DiagnosticSeries[] => {
  const series: DiagnosticSeries[] = []
  const ratioRows = response.link_ratios_values.map(row => row.map(value => value ?? 1))
  const selectedMethods = response.link_ratios_selection ?? []
  const ratioNames = getLinkRatioNames(response, amountType)

  const selectedPoints = response.triangle.dev_labels.map((_, devIndex) => {
    let factor = 1
    for (let index = devIndex; index < selectedMethods.length; index++) {
      const methodIndex = selectedMethods[index] ?? 0
      factor *= ratioRows[methodIndex]?.[index] || 1
    }
    return { dev: devIndex + 1, value: Number(((1 / factor) * 100).toFixed(1)) }
  })
  series.push({ name: 'Selected', points: selectedPoints, selected: true })

  ratioRows.forEach((ratios, index) => {
    const points = cumulativeFactors(ratios).map((factor, devIndex) => ({
      dev: devIndex + 1,
      value: Number(((1 / factor) * 100).toFixed(1)),
    }))
    series.push({
      name: ratioNames[index] || `Method ${index + 1}`,
      points,
    })
  })

  const selectedUltimateNames = getSelectedUltimateNames(response, amountType)
  const ultimateColumnsByName = getUltimateColumns(response, amountType)
  const exclusions = response.triangle_metadata?.selections?.[exclusionKey(amountType)]
  const excludedCells = new Set(
    (exclusions?.excluded_origin_periods ?? []).map(
      (origin, index) => `${origin}\t${exclusions?.excluded_dev_periods?.[index]}`
    )
  )

  const originSeries: DiagnosticSeries[] = []
  response.triangle.origin_labels.forEach((origin, originIndex) => {
    const ultimateName = selectedUltimateNames[originIndex] || 'CL Incurred'
    const ultimate = ultimateColumnsByName[ultimateName]?.[originIndex] ?? 0
    const row = response.triangle.values[originIndex] ?? []
    const points = row.map((value, devIndex) => {
      const isGap =
        value === null ||
        ultimate === 0 ||
        excludedCells.has(`${origin}\t${response.triangle.dev_labels[devIndex]}`)
      return {
        dev: devIndex + 1,
        value: isGap
          ? null
          : Number(((Math.round(value as number) / Math.round(ultimate)) * 100).toFixed(1)),
      }
    })
    originSeries.push({ name: origin, points })
  })

  // Newest origins first — matches Sybil DiagnosticCharts legend order.
  series.push(...originSeries.reverse())

  return series
}

class DiagnosticChartService {
  private userTrianglesCache = new Map<string, Promise<TriangleGroup[]>>()

  getUserTriangles(bounceId: string): Promise<TriangleGroup[]> {
    const cached = this.userTrianglesCache.get(bounceId)
    if (cached) return cached

    const request = api.get<TriangleGroup[]>('/claims/triangles', {
      params: { bounce_id: bounceId },
    }).then(response =>
      response.data.filter(triangle => triangle.id !== DEFAULT_TRIANGLE_ID)
    ).catch(error => {
      this.userTrianglesCache.delete(bounceId)
      throw error
    })
    this.userTrianglesCache.set(bounceId, request)
    return request
  }

  async getTriangulation(
    bounceId: string,
    triangleId: string,
    amountType: DiagnosticAmountType
  ): Promise<TriangulationResponse> {
    const response = await api.get<TriangulationResponse>('/claims/triangulations', {
      params: {
        bounce_id: bounceId,
        triangle_id: triangleId,
        amount_type: amountType,
      },
    })
    return response.data
  }
}

export const diagnosticChartService = new DiagnosticChartService()
